/* b2b-corporate.js - Chemistors corporate gifting page
 * Container text flip, scroll reveal, FAQ accordion, product chips and the
 * enquiry form submit. No dependencies. Every lookup is guarded so this file is
 * inert on pages where the sections are absent.
 */
(() => {
  if (window.__b2bCorporateLoaded) return;
  window.__b2bCorporateLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Must match the width transition on .b2b-flip in b2b-corporate.css.
  const WIDTH_MS = 340;

  // ── Logo marquee ──────────────────────────────────────────────────────────
  // The CSS slides the track by exactly -50%, which is only seamless if the
  // track holds two identical copies of the logos. That second copy is made
  // here rather than in Liquid so the theme editor still sees one block per
  // logo. Clones are stripped of their editor attributes for the same reason:
  // two nodes claiming one block id breaks block selection in the editor.
  function initMarquee(root) {
    const track = root.querySelector('.b2b-marquee__track');
    if (!track || track.dataset.cloned === '1') return;
    track.dataset.cloned = '1';
    if (reducedMotion.matches) return;

    const originals = Array.from(track.children);
    originals.forEach((node) => {
      const clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = '1';
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((child) => {
        child.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      if (clone.tagName === 'A') clone.setAttribute('tabindex', '-1');
      track.appendChild(clone);
    });

    // Hold a steady pixel speed no matter how many logos are in the wall.
    // Measured after the images have laid out - an unloaded <img> is 0px wide,
    // and timing the belt off that makes it sprint.
    const marquee = track.closest('.b2b-marquee') || track.parentElement;
    const setDuration = () => {
      const half = track.scrollWidth / 2;
      if (half > 0) {
        marquee.style.setProperty('--b2b-marquee-duration', `${Math.max(24, Math.round(half / 46))}s`);
      }
    };
    setDuration();

    const images = Array.from(track.querySelectorAll('img'));
    let pending = images.filter((img) => !img.complete).length;
    if (pending > 0) {
      images.forEach((img) => {
        if (img.complete) return;
        const done = () => {
          pending -= 1;
          if (pending <= 0) setDuration();
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }
  }

  // ── Container text flip ───────────────────────────────────────────────────
  // The plate animates its width to the next word, so the headline never jumps
  // the words after it. Width is measured off an invisible ruler that lives
  // inside the plate and therefore inherits the exact heading font.
  function initFlip(root) {
    root.querySelectorAll('.b2b-flip').forEach((el) => {
      if (el.dataset.bound === '1') return;
      el.dataset.bound = '1';

      const word = el.querySelector('.b2b-flip__word');
      if (!word) return;

      const words = String(el.dataset.words || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean);
      if (words.length < 2) return;

      const ruler = document.createElement('span');
      ruler.className = 'b2b-flip__ruler';
      ruler.setAttribute('aria-hidden', 'true');
      el.appendChild(ruler);

      const widthOf = (text) => {
        const style = window.getComputedStyle(el);
        const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        ruler.textContent = text;
        return Math.ceil(ruler.getBoundingClientRect().width + pad);
      };
      const lock = (text) => {
        const width = widthOf(text);
        if (width > 0) el.style.width = `${width}px`;
      };

      let index = 0;
      lock(words[0]);

      // A webfont swapping in after first paint changes the measurement.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => lock(words[index])).catch(() => {});
      }
      window.addEventListener('resize', () => lock(words[index]), { passive: true });

      if (reducedMotion.matches) return;

      // Cross-fade, not fade-out-then-in: the outgoing word is a clone taken out
      // of flow, so the plate is never a blank green box for a few frames.
      const swapTo = (next) => {
        const outgoing = word.cloneNode(true);
        outgoing.classList.remove('is-in');
        outgoing.classList.add('is-out');
        outgoing.setAttribute('aria-hidden', 'true');
        el.insertBefore(outgoing, word);
        window.setTimeout(() => outgoing.remove(), 470);

        word.textContent = next;
        word.classList.remove('is-in');
        void word.offsetWidth; // restart the enter animation
        word.classList.add('is-in');
      };

      window.setInterval(() => {
        index = (index + 1) % words.length;
        const next = words[index];

        // The plate clips its overflow, so the width change and the word swap
        // have to be ordered or something gets its edges cut off.
        //   growing  - widen first, swap once the plate is wide enough
        //   shrinking - swap first, and hold the shrink back until the outgoing
        //               clone has mostly faded, or IT is the thing that clips
        const grows = widthOf(next) > el.getBoundingClientRect().width + 1;
        if (grows) {
          lock(next);
          window.setTimeout(() => swapTo(next), WIDTH_MS);
        } else {
          swapTo(next);
          window.setTimeout(() => lock(next), 250);
        }
      }, 2600);
    });
  }

  // ── Scroll reveal ─────────────────────────────────────────────────────────
  function initReveal(root) {
    const nodes = root.querySelectorAll('.b2b-reveal');
    if (!nodes.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index || 0);
          entry.target.style.transitionDelay = `${Math.min(index, 6) * 70}ms`;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    nodes.forEach((node) => observer.observe(node));
  }

  // ── FAQ accordion ─────────────────────────────────────────────────────────
  function initFaq(root) {
    root.querySelectorAll('.b2b-faq__q').forEach((button) => {
      if (button.dataset.bound === '1') return;
      button.dataset.bound = '1';
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  // ── Enquiry form ──────────────────────────────────────────────────────────
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];

  function collectUtm() {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) utm[key] = value.slice(0, 120);
    });
    return utm;
  }

  function initForm(root) {
    const form = root.querySelector('.b2b-form');
    if (!form || form.dataset.bound === '1') return;
    form.dataset.bound = '1';

    const card = root.querySelector('.b2b-form-card');
    const errorBox = root.querySelector('.b2b-form__error');
    const submit = form.querySelector('[data-submit]');
    const startedAt = form.querySelector('[name="formStartedAt"]');
    const selected = new Set();

    // Stamp the clock on the first real interaction, not on page load: a page
    // left open in a tab would otherwise look like a very patient human.
    const stamp = () => {
      if (startedAt && !startedAt.value) startedAt.value = String(Date.now());
    };
    ['focusin', 'input', 'pointerdown'].forEach((event) => form.addEventListener(event, stamp, { once: true }));

    form.querySelectorAll('.b2b-chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        const value = chip.dataset.value || chip.textContent.trim();
        const on = chip.getAttribute('aria-pressed') === 'true';
        chip.setAttribute('aria-pressed', on ? 'false' : 'true');
        if (on) selected.delete(value); else selected.add(value);
      });
    });

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest' });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      if (errorBox) errorBox.textContent = '';

      const data = new FormData(form);
      const payload = {};
      data.forEach((value, key) => {
        const text = String(value).trim();
        if (text) payload[key] = text;
      });
      payload.productInterest = Array.from(selected);
      payload.sourcePage = window.location.pathname;
      payload.utm = collectUtm();

      const endpoint = form.dataset.endpoint;
      if (!endpoint) {
        showError('This form is not connected yet. Please email chemistors@gmail.com and we will pick it up.');
        return;
      }

      if (submit) submit.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          showError(result.error || 'We could not send that. Please check the highlighted fields and try again.');
          return;
        }

        // A honeypot or too-fast submission returns 200 with a null id. Show the
        // same success state either way so a bot learns nothing from the page.
        const reference = result.id ? String(result.id) : '';
        if (card) {
          card.innerHTML = `<div class="b2b-success" role="status">
            <div class="b2b-success__tick" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h3>Enquiry received</h3>
            <p>Thank you. Our corporate team will be in touch within one working day with options, samples and slab pricing. A confirmation is on its way to your inbox.</p>
            ${reference ? `<div class="b2b-success__ref">Reference ${reference}</div>` : ''}
          </div>`;
          card.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        }
      } catch (error) {
        showError('Network trouble at our end. Please try again, or email chemistors@gmail.com.');
      } finally {
        if (submit) submit.removeAttribute('aria-busy');
      }
    });
  }

  // ── Smooth scroll for the hero CTA ────────────────────────────────────────
  function initAnchors(root) {
    root.querySelectorAll('a[href="#b2b-enquiry"]').forEach((link) => {
      if (link.dataset.bound === '1') return;
      link.dataset.bound = '1';
      link.addEventListener('click', (event) => {
        const target = document.getElementById('b2b-enquiry');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
        const firstField = target.querySelector('input, select, textarea');
        if (firstField) window.setTimeout(() => firstField.focus({ preventScroll: true }), 500);
      });
    });
  }

  function boot(scope) {
    const root = scope || document;
    initFlip(root);
    root.querySelectorAll('.b2b-marquee').forEach(initMarquee);
    initReveal(root);
    initFaq(root);
    initForm(root);
    initAnchors(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  // Shopify theme editor re-renders a single section without a page reload.
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
